import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, MinusCircle, RefreshCw, Activity, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NICE = {
  mongodb: { label: 'MongoDB', tag: 'database' },
  sendgrid: { label: 'SendGrid', tag: 'email' },
  twilio: { label: 'Twilio', tag: 'sms / mfa' },
  easypost: { label: 'EasyPost', tag: 'shipping' },
  stripe: { label: 'Stripe', tag: 'payments' },
  sellegard: { label: 'SelleGard Freight', tag: 'shipping' },
  shipstation: { label: 'ShipStation', tag: 'shipping' },
  emergent_llm: { label: 'Emergent LLM', tag: 'ai' },
};

export default function AdminHealthPage() {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/health/latest`, { headers: authHeaders });
    if (r.ok) setData(await r.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${API}/health/run-now`, { method: 'POST', headers: authHeaders });
      if (r.ok) setData(await r.json());
    } finally { setRunning(false); }
  };

  const results = data?.results || {};
  const summary = data?.summary || { green: 0, red: 0, skipped: 0 };
  const entries = Object.entries(results).sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-health-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-500" /> Integration Health
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Runs automatically every day at 07:00 UTC. Last check {data?.started_at ? new Date(data.started_at).toLocaleString() : '—'}.
          </p>
        </div>
        <Button onClick={runNow} disabled={running} data-testid="runnow-btn">
          {running ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Run now
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Healthy" value={summary.green} tone="emerald" icon={CheckCircle2} testid="stat-green" />
        <Stat label="Failing" value={summary.red} tone="rose" icon={XCircle} testid="stat-red" />
        <Stat label="Skipped" value={summary.skipped} tone="slate" icon={MinusCircle} testid="stat-skipped" />
      </div>

      {summary.red > 0 && (
        <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm flex items-start gap-2" data-testid="red-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>
            {summary.red} integration{summary.red > 1 ? 's are' : ' is'} failing. Fix the key(s) below and click <b>Run now</b>.
          </span>
        </div>
      )}

      {/* Per-integration cards */}
      <div className="grid sm:grid-cols-2 gap-3">
        {entries.map(([name, r]) => {
          const nice = NICE[name] || { label: name, tag: '' };
          const status = r.ok === true ? 'green' : r.ok === false ? 'red' : 'skipped';
          const cls = {
            green: 'border-emerald-200 bg-emerald-50/50',
            red: 'border-rose-200 bg-rose-50/60',
            skipped: 'border-slate-200 bg-slate-50/50',
          }[status];
          const Icon = status === 'green' ? CheckCircle2 : status === 'red' ? XCircle : MinusCircle;
          const iconCls = { green: 'text-emerald-600', red: 'text-rose-600', skipped: 'text-slate-400' }[status];
          return (
            <div key={name} className={`rounded-xl border p-4 ${cls}`} data-testid={`integ-${name}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${iconCls}`} />
                  <div>
                    <p className="font-semibold text-sm">{nice.label}</p>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{nice.tag}</p>
                  </div>
                </div>
                <div className="text-right">
                  {r.status && <p className="text-xs font-mono text-slate-600">HTTP {r.status}</p>}
                  {typeof r.latency_ms === 'number' && <p className="text-[10px] text-slate-500">{r.latency_ms}ms</p>}
                </div>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-mono break-all">{(r.detail || '').slice(0, 240)}</p>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-400">
        SendGrid probe uses <code>sandbox_mode</code> — validates end-to-end without sending a real email or consuming credits.
      </p>
    </div>
  );
}

const Stat = ({ label, value, tone, icon: Icon, testid }) => {
  const c = {
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    rose: 'text-rose-600 bg-rose-50 border-rose-200',
    slate: 'text-slate-600 bg-slate-50 border-slate-200',
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${c}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide">{label}</p>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-3xl font-bold tabular-nums mt-2">{value}</p>
    </div>
  );
};
